import Modal from "@/components/atoms/Modal";
import Select from "@/components/atoms/Select";
import SoftBadge from "@/components/atoms/SoftBadge";
import {
  INVALID_FORM_MESSAGE,
  INVALID_MAXIMUM_CARACTERE_MESSAGE,
  INVALID_MINIMUM_CARACTERE_MESSAGE,
  INVALID_NAME_MESSAGE,
} from "@/constants/validatorMessages";
import { USER_ROLES } from "@/interfaces/IUser";
import { IModalProps } from "@/interfaces/Modal";
import MontageMeetingStatus, {
  MontageMeetingStatusForm,
} from "@/interfaces/MontageMeetingStatus";
import {
  useStoreMontageMeetingStatusMutation,
  useUpdateMontageMeetingStatusMutation,
} from "@/services/montageMeetingStatuses";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Col, Form, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";

interface Props extends Omit<IModalProps, "children" | "onPress"> {
  onHide?: () => void;
}

const DEFAULTS = { backgroundColor: "#1e1a55", textColor: "#ffffff" };

/**
 * Création et édition d'un statut de rendez-vous montage.
 *
 * Une seule modale pour les deux : le formulaire est identique, et un écran
 * d'édition séparé n'apporterait qu'un aller-retour de navigation.
 */
const StoreMontageMeetingStatusModal: ForwardRefRenderFunction<
  { showOrHiddenModal: (status?: MontageMeetingStatus) => void },
  Props
> = ({ onHide, ...props }, ref) => {
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const [editedId, setEditedId] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<MontageMeetingStatusForm>({ defaultValues: DEFAULTS });

  const showOrHiddenModal = (status?: MontageMeetingStatus) => {
    setEditedId(status?.id ?? null);
    reset(
      status
        ? {
            name: status.name,
            backgroundColor: status.backgroundColor,
            textColor: status.textColor,
            role: USER_ROLES.find((role) => role.value === status.role) ?? null,
          }
        : DEFAULTS,
    );
    ModalRef.current?.showOrHiddenModal();
  };

  useImperativeHandle(ref, () => ({ showOrHiddenModal }));

  const [storeAsync] = useStoreMontageMeetingStatusMutation();
  const [updateAsync] = useUpdateMontageMeetingStatusMutation();

  const onSubmit: SubmitHandler<MontageMeetingStatusForm> = async ({
    role,
    ...data
  }) => {
    const payload = { ...data, role: role?.value ?? null };

    toast.promise(
      editedId
        ? updateAsync([editedId, payload]).unwrap()
        : storeAsync(payload).unwrap(),
      {
        pending: editedId
          ? "Modification du statut en cours..."
          : "Ajout du statut en cours...",
        success: {
          render() {
            reset(DEFAULTS);
            setEditedId(null);
            ModalRef.current?.showOrHiddenModal();
            return editedId
              ? "Le statut a bien été modifié 🤩"
              : "Le statut a bien été ajouté 🤩";
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            if (data?.status === 422) {
              return "Ce nom est déjà utilisé.";
            }
            return "Une erreur est survenue";
          },
        },
      },
    );
  };

  const onError: SubmitErrorHandler<MontageMeetingStatusForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <Modal
      ref={ModalRef}
      {...props}
      requiredLabel
      onSubmit={handleSubmit(onSubmit, onError)}
      onHide={onHide}
      beforeFooter={
        <Controller
          control={control}
          name="backgroundColor"
          render={({ field: backgroundColor }) => (
            <Controller
              control={control}
              name="textColor"
              render={({ field: textColor }) => (
                <Controller
                  control={control}
                  name="name"
                  render={({ field: name }) => (
                    <SoftBadge
                      className="me-auto"
                      textClassName="h6"
                      bg={backgroundColor.value}
                      textColor={textColor.value}
                    >
                      {name.value || "Mon statut"}
                    </SoftBadge>
                  )}
                />
              )}
            />
          )}
        />
      }
    >
      <Row className="gx-3">
        <Col lg={12}>
          <Form.Group className="mb-3">
            <Form.Label>
              Nom <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              placeholder="ex : À traiter"
              type="text"
              {...register("name", {
                required: { value: true, message: INVALID_NAME_MESSAGE },
                minLength: { value: 2, message: INVALID_MINIMUM_CARACTERE_MESSAGE },
                maxLength: { value: 255, message: INVALID_MAXIMUM_CARACTERE_MESSAGE },
              })}
              isInvalid={!!errors.name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.name?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={12}>
          <Form.Group className="mb-3">
            {/* Champ repris des statuts d'entreprise, où il pilote les
                permissions. Ici il est stocké mais n'a encore aucun effet :
                le dire évite de croire qu'il restreint quoi que ce soit. */}
            <Form.Label>Pour le rôle (sans effet pour l'instant) :</Form.Label>
            <Controller
              control={control}
              name="role"
              render={({ field }) => (
                <Select
                  options={USER_ROLES}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  value={field.value}
                  onChange={field.onChange}
                  isClearable
                />
              )}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="gx-3">
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Couleur du texte <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              type="color"
              {...register("textColor", { required: "Ce champ est obligatoire" })}
              isInvalid={!!errors.textColor}
            />
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Couleur de fond <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              type="color"
              {...register("backgroundColor", {
                required: "Ce champ est obligatoire",
              })}
              isInvalid={!!errors.backgroundColor}
            />
          </Form.Group>
        </Col>
      </Row>
    </Modal>
  );
};

export default forwardRef(StoreMontageMeetingStatusModal);
